import { createContext, useContext, useEffect, useState } from "react";
import i18n from "@/lib/i18n";
import {
  getSidebarCollapsedFromCookie,
  setSidebarCollapsedCookie,
} from "@/lib/cookies";

type Theme = "light" | "dark" | "system";
type SupportedLanguage = "en" | "es" | "sr";

interface PreferenceContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  singleLanguageMode: boolean;
  singleThemeMode: boolean;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const PreferenceContext = createContext<PreferenceContextType | undefined>(undefined);

interface PreferenceProviderProps {
  children: React.ReactNode;
  singleLanguage?: SupportedLanguage;
}

export function PreferenceProvider({ children }: PreferenceProviderProps) {
  // Dark mode is dropped for the Pošte Srpske brand — always light.
  const theme: Theme = "light";
  const resolvedTheme = "light" as const;
  const singleThemeMode = true;
  const setTheme = () => {};

  // Ensure the dark class is never present (e.g. stale cookie/markup).
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  // Serbian (Latin) is the primary — and only — UI language for this product.
  // Forced regardless of any stale language cookie; the switcher is hidden.
  const language: SupportedLanguage = "sr";
  const singleLanguageMode = true;
  const setLanguage = () => {};

  useEffect(() => {
    if (i18n.language !== "sr") {
      i18n.changeLanguage("sr");
    }
  }, []);

  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    return getSidebarCollapsedFromCookie();
  });

  const setSidebarCollapsed = (collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
    setSidebarCollapsedCookie(collapsed);
  };

  return (
    <PreferenceContext.Provider
      value={{
        theme,
        setTheme,
        resolvedTheme,
        language,
        setLanguage,
        singleLanguageMode,
        singleThemeMode,
        sidebarCollapsed,
        setSidebarCollapsed,
      }}
    >
      {children}
    </PreferenceContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferenceContext);
  if (!context) {
    throw new Error("usePreferences must be used within a PreferenceProvider");
  }
  return context;
}
